import { Navigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useCanViewTailscaleMesh } from '@/hooks/useCanViewTailscaleMesh';
import { ApiError } from '@/lib/api';
import { MeshNodesTable } from '../components/MeshNodesTable';
import { useMeshNodes, useRefreshMeshNodes } from '../hooks/useMeshNodes';

/** The API client rewrites 5xx to a generic maintenance message; the server's reason is the useful one. */
function meshErrorMessage(error: Error): string {
  const serverMessage = error instanceof ApiError ? (error.data as { error?: string } | undefined)?.error : undefined;
  return serverMessage || error.message;
}

export default function MeshPage() {
  const canViewMesh = useCanViewTailscaleMesh();
  const meshQuery = useMeshNodes(canViewMesh);
  const refreshMesh = useRefreshMeshNodes();

  // Mesh machines are ATEK-internal assets; the nav item is hidden for
  // non-staff and this guard covers direct URL access.
  if (!canViewMesh) {
    return <Navigate to="/operations/gateways" replace />;
  }

  return (
    <div className="container mx-auto space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">VPN Mesh</h1>
          <p className="text-sm text-muted-foreground">
            Every machine registered on the ATEK Headscale tailnet (hs.atek.io)
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshMesh}
          disabled={meshQuery.isFetching}
        >
          <RefreshCw className={`mr-1 h-4 w-4 ${meshQuery.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      {meshQuery.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the VPN mesh</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              {meshErrorMessage(meshQuery.error)}
              {meshQuery.data ? ' Showing the last known data.' : ''}
            </span>
            <Button variant="outline" size="sm" onClick={refreshMesh} disabled={meshQuery.isFetching}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {/* A failed first load has no data: an empty table would read as an empty mesh. */}
      {(!meshQuery.isError || meshQuery.data) && (
        <MeshNodesTable nodes={meshQuery.data || []} loading={meshQuery.isLoading} />
      )}
    </div>
  );
}
